import * as React from "react";

import { AgentThreadFeedback } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/AgentThreadFeedback";
import { AgentThreadMessageComponents } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/AgentThreadMessageComponents.tsx";
import { AssistantToolUseContent } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/AssistantToolUseContent.tsx";
import { ForkAgentThreadButton } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/ForkAgentThreadButton";
import { MessageTimestamp } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/MessageTimestamp";
import { ToolApprovalCard } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/ToolApprovalCard";
import { ToolUseGroup } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/ToolUseGroup";
import { useAIAgentThread } from "@/AgentThread/AgentThreadSidebar/AIAgentThreadContext";
import { ThinkingIndicator } from "@/shared/components";
import {
  AgentThreadMessageStatus,
  AgentThreadRecord,
} from "@/shared/domain/agentThread";
import {
  type AgentThreadGoalRecord,
  type AgentThreadMessage,
  type AgentThreadToolUseContent,
  AgentThreadRole,
  AgentThreadStatus,
  isAgentThreadTextContent,
  isAgentThreadToolResultContent,
  isAgentThreadToolUseContent,
} from "@/shared/domain/agentThread/AgentThreadRecord.ts";
import type { PendingToolApproval } from "@/shared/domain/agentThread/hooks/useClientToolExecution";

import styles from "./AgentThreadByIdView.module.css";
import { ContextShiftIndicator } from "./ContextShiftIndicator/ContextShiftIndicator";
import { GoalsStrip } from "./GoalsStrip/GoalsStrip";
import { groupToolMessages, type MessageGroup } from "./groupToolMessages";
import { ScrollToBottomButton } from "./ScrollToBottomButton";
import { TaskPanel } from "./TaskPanel/TaskPanel";
import { TaskSection } from "./TaskSection/TaskSection";
import {
  buildTaskRegions,
  groupSegmentsByStartedTask,
  HIDDEN_TASK_TOOL_NAMES,
  isTaskRegionBoundary,
  pruneEmptyTaskBlocks,
  sectionTaskIdForMessage,
} from "./taskSections";
import { useActiveTaskOnScroll } from "./useActiveTaskOnScroll";
import { useContextShifts } from "./useContextShifts";
import { useScrollSentinel } from "./useScrollSentinel";
import { useSpacer } from "./useSpacer";

/**
 * Whether a message's role/content earns a "sent at" caption: user messages
 * and assistant messages with visible prose do; tool-use-only assistant
 * messages and roboto-role tool results don't (a time per tool call is
 * noise). Says nothing about whether the message carries a `created` value.
 */
function shouldShowMessageTimestamp(message: AgentThreadMessage): boolean {
  if (message.role === AgentThreadRole.USER) {
    return true;
  }
  return (
    message.role === AgentThreadRole.ASSISTANT &&
    message.content.some(isAgentThreadTextContent)
  );
}

/**
 * Determines if feedback should be shown after a message at the given index.
 * Feedback is shown after assistant messages that end with text content when:
 * 1. The next message is from the user (historical feedback)
 * 2. It's the last message and it's the user's turn (current turn feedback)
 */
function shouldShowFeedback(
  agentThread: AgentThreadRecord,
  messageSequenceNum: number,
): { show: boolean; lastAssistantText?: string } {
  const message = agentThread.messages[messageSequenceNum];
  const nextMessage = agentThread.messages[messageSequenceNum + 1];
  const isLastMessage = messageSequenceNum === agentThread.messages.length - 1;

  // Only show feedback after assistant messages
  if (message.role !== AgentThreadRole.ASSISTANT) {
    return { show: false };
  }

  // Must have content ending with text
  if (message.content.length === 0) {
    return { show: false };
  }

  const lastContent = message.content[message.content.length - 1];
  if (!isAgentThreadTextContent(lastContent)) {
    return { show: false };
  }

  // Show if next message is from user, or if it's the last message and user's turn
  const showAfterHistory =
    nextMessage && nextMessage.role === AgentThreadRole.USER;
  const showAtEnd =
    isLastMessage && agentThread.status === AgentThreadStatus.USER_TURN;

  if (showAfterHistory || showAtEnd) {
    return { show: true, lastAssistantText: lastContent.text };
  }

  return { show: false };
}

/**
 * Width (px) of the chat's scroll viewport at which the task panel switches
 * from the tight strip to the wide table-of-contents rail. Kept in sync with
 * the `@container thread (min-width: 1024px)` query in
 * AgentThreadByIdView.module.css that moves the rail beside the conversation;
 * the JS branch (which tasks to show, click/expand affordances) and the CSS
 * branch (where the rail sits) must flip together.
 */
const WIDE_LAYOUT_MIN_WIDTH_PX = 1024;

/**
 * Track whether the chat is wide enough for the side-by-side TOC layout. We
 * measure the scroll viewport itself rather than the window because the chat
 * renders both in a narrow docked sidebar and as a wide full-page view.
 */
function useIsWideLayout(ref: React.RefObject<HTMLElement>): boolean {
  const [isWide, setIsWide] = React.useState(false);

  React.useLayoutEffect(
    function observeWidth() {
      const element = ref.current;
      if (!element) {
        return;
      }
      const update = () =>
        setIsWide(element.clientWidth >= WIDE_LAYOUT_MIN_WIDTH_PX);
      update();
      const observer = new ResizeObserver(update);
      observer.observe(element);
      return () => observer.disconnect();
    },
    [ref],
  );

  return isWide;
}

/** Why a retry did not run, and the failure notice it was asked from. */
export interface RetryRefusal {
  threadId: string;
  /** Index of the failure notice in the thread's messages. */
  messageSequenceNum: number;
  explanation: string;
}

interface AgentThreadByIdViewProps {
  agentThread: AgentThreadRecord;
  isCancelling?: boolean;
  onRetry?: (messageSequenceNum: number) => void;
  /** Shown on the notice it names, after the server refused a retry from it. */
  retryRefusal?: RetryRefusal;
  pendingApproval?: PendingToolApproval | null;
  onApprove?: () => void;
  onDecline?: () => void;
  /** See {@link ToolApprovalCard}'s prop of the same name. */
  onEnableAutoMode?: () => void;
  /** See {@link ToolApprovalCard}'s prop of the same name. */
  savedPermissionsUnavailable?: boolean;
}

export const AgentThreadByIdView = ({
  agentThread,
  isCancelling = false,
  onRetry,
  retryRefusal,
  pendingApproval,
  onApprove,
  onDecline,
  onEnableAutoMode,
  savedPermissionsUnavailable,
}: AgentThreadByIdViewProps) => {
  // ``renderMessageDebugControl`` and ``scrollToMessageSequenceNum`` are
  // admin-only and arrive via context so they work whether this view is
  // rendered interactively or read-only.
  const {
    renderMessageDebugControl,
    renderMessageKnowledgeGraphControl,
    scrollToMessageSequenceNum,
  } = useAIAgentThread();

  // Spacer hook for hoisting user messages to top of viewport
  const { scrollContainerRef, spacerRef, messageToHoistRef, recomputeSpacer } =
    useSpacer();

  // Scroll sentinel hook for detecting when user has scrolled up
  const scrollSentinel = useScrollSentinel(scrollContainerRef);

  // Wide chat → task table-of-contents beside the conversation; narrow chat →
  // tight strip of open work above it.
  const isWide = useIsWideLayout(scrollContainerRef);

  // Scroll-spy: which task's section the reader is currently looking at, so the
  // panel can highlight it. Recompute when the message set changes (anchors
  // appear/disappear) on top of the hook's own scroll tracking.
  const activeTaskId = useActiveTaskOnScroll(
    scrollContainerRef,
    agentThread.messages.length,
  );

  // Click-to-jump: a selected task's section must be force-expanded — its body,
  // and the scroll anchor inside, are unmounted while the user has it collapsed.
  // Every started task (active or concluded) renders in a collapsible section,
  // so any of them may need this.
  const [jumpTargetTaskId, setJumpTargetTaskId] = React.useState<
    number | undefined
  >(undefined);
  // A fresh object per selection so re-selecting the same task re-fires the
  // scroll effect below.
  const [jumpRequest, setJumpRequest] = React.useState<{
    taskId: number;
  } | null>(null);

  const handleSelectTask = React.useCallback(
    (taskId: number) => {
      const task = agentThread.tasks?.find(
        (candidate) => candidate.task_id === taskId,
      );
      // Pending / unknown tasks own no conversation region to jump to.
      if (!task || !task.start) {
        return;
      }
      // Force the target section open: its body (and the scroll anchor) is
      // unmounted while collapsed, whether the task is active or concluded.
      setJumpTargetTaskId(taskId);
      setJumpRequest({ taskId });
    },
    [agentThread.tasks],
  );

  // Scroll to the selected task's anchor after the (possibly newly expanded)
  // section has committed. The first `[data-task-id]` match is the task's
  // section header (the section root carries `data-task-id`).
  React.useLayoutEffect(
    function scrollToSelectedTask() {
      if (jumpRequest === null) {
        return;
      }
      const anchor = scrollContainerRef.current?.querySelector(
        `[data-task-id="${jumpRequest.taskId}"]`,
      );
      anchor?.scrollIntoView({ behavior: "smooth", block: "start" });
      // Release the force-open lock now that the jump has landed. The targeted
      // section has already latched the open state into its own toggle (a child
      // layout effect that ran before this one), so it stays open — but it is
      // no longer pinned, so the user can collapse it again. The body stays
      // mounted, so clearing here doesn't yank the scroll anchor out mid-scroll.
      setJumpTargetTaskId(undefined);
    },
    [jumpRequest, scrollContainerRef],
  );

  // Track user message count to detect when a NEW user message is added
  const userMessageCountRef = React.useRef<number>(0);

  // Track first run to distinguish mount from subsequent updates
  const isFirstRunRef = React.useRef(true);

  // Flag to suppress scroll button flicker during auto-scroll
  const [isAutoScrolling, setIsAutoScrolling] = React.useState(false);

  // Recompute spacer when messages change (streaming, new messages) or when
  // the thread transitions between turns. We attach the hoist ref only while
  // the AI is generating; the status change recompute clears the spacer at
  // rest so the user can't scroll past all messages into an empty viewport.
  React.useLayoutEffect(recomputeSpacer, [
    agentThread.messages,
    agentThread.status,
    recomputeSpacer,
  ]);

  // Handle scroll on mount and when new user messages are added
  React.useLayoutEffect(
    function scrollOnMessagesChange() {
      const currentUserMessageCount = agentThread.messages.filter(
        (m) => m.role === AgentThreadRole.USER,
      ).length;

      if (isFirstRunRef.current) {
        // First mount: initialize count and scroll to bottom
        isFirstRunRef.current = false;
        userMessageCountRef.current = currentUserMessageCount;

        // Only scroll after browser paint to ensure layout has fully applied
        requestAnimationFrame(function scrollToBottom() {
          scrollSentinel.ref.current?.scrollIntoView({
            block: "end",
            behavior: "instant",
          });
        });

        // Skip the rest on first run
        return;
      }

      if (currentUserMessageCount > userMessageCountRef.current) {
        // New user message: scroll to it with smooth animation
        userMessageCountRef.current = currentUserMessageCount;

        // Suppress button during scroll animation
        setIsAutoScrolling(true);

        messageToHoistRef.current?.scrollIntoView({
          block: "start",
          behavior: "smooth",
        });

        // Clear flag after scroll animation completes (estimate ~300ms)
        setTimeout(() => {
          setIsAutoScrolling(false);
        }, 300);
      }
    },
    [agentThread.messages, messageToHoistRef, scrollSentinel.ref],
  );

  const thinking = React.useMemo(() => {
    if (agentThread.messages.length === 0) {
      return true;
    }

    if (agentThread.status === AgentThreadStatus.USER_TURN) {
      return false;
    }

    const lastMessage = agentThread.messages.at(-1);

    // If we're currently addressing a tool use request, that has its own loading indicator, so we shouldn't
    // double-display them
    if (lastMessage) {
      const lastContent = lastMessage?.content.at(-1);

      if (isAgentThreadToolUseContent(lastContent)) {
        return false;
      }
    }

    return lastMessage?.status === AgentThreadMessageStatus.COMPLETED;
  }, [agentThread]);

  // Find the sequence number of the last user message for ref attachment
  const lastUserMessageSequenceNum = React.useMemo(() => {
    for (let i = agentThread.messages.length - 1; i >= 0; i--) {
      if (agentThread.messages[i].role === AgentThreadRole.USER) {
        return i;
      }
    }
    return -1;
  }, [agentThread.messages]);

  // Detect context shifts in the message list (incrementally cached)
  const contextShifts = useContextShifts(agentThread.messages);

  // Goals are session-wide; group them by message_sequence_num so each turn
  // can render its own strip. Empty / missing goals -> empty map -> no strips.
  const goalsByMessageIdx = React.useMemo(() => {
    const map = new Map<number, AgentThreadGoalRecord[]>();
    for (const goal of agentThread.goals ?? []) {
      const list = map.get(goal.message_sequence_num) ?? [];
      list.push(goal);
      map.set(goal.message_sequence_num, list);
    }
    return map;
  }, [agentThread.goals]);

  // ``isGenerating`` covers every non-user turn, not just ROBOTO_TURN, so the
  // hoist-to-top spacer below stays attached while a turn passes through
  // CLIENT_TOOL_TURN. The previous form (``=== ROBOTO_TURN``) caused the
  // spacer to collapse and re-expand each time the agent paused on a client
  // tool, which the user perceived as a layout flicker mid-conversation.
  const isGenerating =
    agentThread.messages.length > 0 &&
    (agentThread.status === AgentThreadStatus.ROBOTO_TURN ||
      agentThread.status === AgentThreadStatus.CLIENT_TOOL_TURN);

  // Contiguous task regions (one per started task) drive both the tool-grouping
  // boundary (so no tool group spans two tasks) and the collapsible assignment.
  const taskRegions = React.useMemo(
    () => buildTaskRegions(agentThread.tasks),
    [agentThread.tasks],
  );

  const messageGroups = React.useMemo(
    () =>
      groupToolMessages(agentThread.messages, (index) =>
        isTaskRegionBoundary(taskRegions, index),
      ),
    [agentThread.messages, taskRegions],
  );

  const pendingToolUses = React.useMemo((): AgentThreadToolUseContent[] => {
    const lastMessage = agentThread.messages.at(-1);
    const precedingMessage = agentThread.messages.at(-2);

    if (
      lastMessage?.role !== AgentThreadRole.ROBOTO ||
      lastMessage.status !== AgentThreadMessageStatus.GENERATING ||
      precedingMessage?.role !== AgentThreadRole.ASSISTANT
    ) {
      return [];
    }

    const completedToolUseIds = new Set(
      lastMessage.content
        .filter(isAgentThreadToolResultContent)
        .map((content) => content.tool_use_id),
    );

    return precedingMessage.content
      .filter(isAgentThreadToolUseContent)
      .filter((toolUse) => !completedToolUseIds.has(toolUse.tool_use_id));
  }, [agentThread.messages]);

  // ``owningTaskId`` is the task whose section the segment renders in (undefined
  // for loose segments); it feeds the ``data-task-id`` anchors so the task
  // panel's scroll-spy and click-to-jump always agree with the visual placement
  // — including a handoff message's closing text, which renders in the ENDING
  // task's section even though the next task's region starts on that message.
  const renderSegment = (
    segment: MessageGroup,
    owningTaskId?: number,
  ): React.ReactNode => {
    if (segment.type === "toolGroup") {
      // Hide the task-list machinery (add_tasks/start_task/...) from the
      // transcript; skip a group entirely when nothing real is left.
      const visibleToolResults = segment.toolResults.filter(
        (toolResult) => !HIDDEN_TASK_TOOL_NAMES.has(toolResult.tool_name),
      );
      if (visibleToolResults.length === 0) {
        return null;
      }
      return (
        <article
          key={`tool-group-${segment.messageIndices[0]}`}
          // Anchor for the task panel's scroll-spy / click-to-jump (omitted
          // when the segment belongs to no started task).
          data-task-id={owningTaskId}
          className={styles.messageArticle}
        >
          <ToolUseGroup
            threadId={agentThread.thread_id}
            toolResults={visibleToolResults}
            toolInputs={segment.toolInputs}
          />
        </article>
      );
    }

    return segment.messages.map((message, segmentIndex) => {
      const messageSequenceNum = segment.messageIndices[segmentIndex];
      const feedback = shouldShowFeedback(agentThread, messageSequenceNum);
      const isLastMessage =
        messageSequenceNum === agentThread.messages.length - 1;
      const isLastUserMessage =
        messageSequenceNum === lastUserMessageSequenceNum;

      // Check if this user message has a context shift to show above it
      const shiftContext = contextShifts.get(messageSequenceNum);
      const goalsForTurn = goalsByMessageIdx.get(messageSequenceNum);

      // Admin-only debug control; null for messages with no
      // recorded Bedrock call (user messages, pre-capture turns).
      const messageDebugControl =
        renderMessageDebugControl?.(messageSequenceNum);

      // Admin-only knowledge-graph control — only ever offered on a user
      // message (the turn's request), never on the assistant's own reply.
      const messageKnowledgeGraphControl =
        message.role === AgentThreadRole.USER
          ? renderMessageKnowledgeGraphControl?.(messageSequenceNum)
          : null;

      // `created` is absent only on the delta merge's transient gap-filler
      // placeholders (see AgentThreadService.applyDelta), which render no
      // content either; it is on every message the server returns.
      const showSentAt =
        shouldShowMessageTimestamp(message) && message.created !== undefined;
      const showActions = Boolean(
        feedback.show || messageDebugControl || messageKnowledgeGraphControl,
      );

      return (
        <article
          key={messageSequenceNum}
          // Stable selector for "scroll to message N" — querying
          // ``article``-by-position is wrong because tool groups,
          // pending tool uses, and approval cards also render as
          // ``<article>`` elements and shift the index. Messages inside a
          // concluded task only render once that section is open, so the view
          // auto-expands the section owning ``scrollToMessageSequenceNum`` (see
          // ``targetTaskId``) to keep this node present for the scroll query.
          data-message-sequence-num={messageSequenceNum}
          // Anchor for the task panel's scroll-spy / click-to-jump (omitted
          // when the message belongs to no started task).
          data-task-id={owningTaskId}
          ref={
            // Hoist-to-top is a live-generation affordance only.
            // Attaching the ref at rest keeps the viewport-sized
            // spacer alive and lets the user scroll past the last
            // message into whitespace.
            isLastUserMessage && isGenerating ? messageToHoistRef : undefined
          }
          className={styles.messageArticle}
        >
          {shiftContext && <ContextShiftIndicator context={shiftContext} />}
          {goalsForTurn && goalsForTurn.length > 0 && (
            <GoalsStrip goals={goalsForTurn} />
          )}
          <AgentThreadMessageComponents
            message={message}
            isLastMessageInAgentThread={isLastMessage}
            onRetry={
              // Retry only while the thread waits on the person: once a
              // retry is under way the thread is working, and the button goes.
              onRetry && agentThread.status === AgentThreadStatus.USER_TURN
                ? () => onRetry(messageSequenceNum)
                : undefined
            }
            retryRefusal={
              retryRefusal?.threadId === agentThread.thread_id &&
              retryRefusal.messageSequenceNum === messageSequenceNum
                ? retryRefusal.explanation
                : undefined
            }
            isCancelling={isCancelling}
            pendingApprovalToolUseId={pendingApproval?.toolUse.tool_use_id}
          />
          {(showSentAt || showActions) && (
            // One footer row: the sent time at the start, the fork/feedback
            // controls at the end. Stacking them as two rows put a full
            // message gap between the time and the thumbs.
            <div
              className={styles.messageFooter}
              data-align={
                message.role === AgentThreadRole.USER ? "end" : "start"
              }
            >
              {showSentAt && message.created !== undefined && (
                <MessageTimestamp created={message.created} />
              )}
              {showActions && (
                <div className={styles.messageActions}>
                  {feedback.show && (
                    <>
                      <ForkAgentThreadButton
                        threadId={agentThread.thread_id}
                        agentThreadOrgId={agentThread.org_id}
                        messageSequenceNum={messageSequenceNum}
                        disabled={
                          message.status !== AgentThreadMessageStatus.COMPLETED
                        }
                      />
                      <AgentThreadFeedback
                        threadId={agentThread.thread_id}
                        agentThreadOrgId={agentThread.org_id}
                        messageSequenceNum={messageSequenceNum}
                        messageStatus={message.status}
                      />
                    </>
                  )}
                  {messageDebugControl}
                  {messageKnowledgeGraphControl}
                </div>
              )}
            </div>
          )}
        </article>
      );
    });
  };

  // Group each started task's turns under one collapsible (created when the task
  // starts, not when it completes); planning before any task and the trailing
  // wrap-up after a task's end render inline. Drop any task whose segments are
  // all hidden task machinery so it doesn't surface as a collapsible with an
  // empty body until its first visible turn arrives.
  const renderBlocks = pruneEmptyTaskBlocks(
    groupSegmentsByStartedTask(messageGroups, taskRegions),
  );

  // When the admin explorer asks to scroll to a message that lives inside a
  // task section, that section's collapsible must be open or the target node is
  // unmounted and the scroll silently no-ops. Auto-expand the owning section.
  const adminTargetTaskId =
    scrollToMessageSequenceNum === undefined
      ? undefined
      : sectionTaskIdForMessage(renderBlocks, scrollToMessageSequenceNum);

  // A task section is force-expanded when either the admin explorer or a
  // task-panel click targets it. The panel click takes precedence.
  const targetTaskId = jumpTargetTaskId ?? adminTargetTaskId;

  return (
    <div ref={scrollContainerRef} className={styles.scrollContainer}>
      <div className={styles.threadQuery}>
        <div className={styles.centeringContainer}>
          {agentThread.tasks && agentThread.tasks.length > 0 && (
            // Pulled out of the message column so the wide layout can float it
            // beside the conversation as a table of contents. On a narrow chat
            // (docked sidebar) it falls back to a sticky strip above the messages
            // — the `thread` container query in the stylesheet drives the switch.
            <div className={styles.taskRail}>
              <TaskPanel
                tasks={agentThread.tasks}
                layout={isWide ? "wide" : "tight"}
                activeTaskId={activeTaskId}
                onSelectTask={handleSelectTask}
              />
            </div>
          )}
          <div
            className={styles.contentContainer}
            role="log"
            aria-label="Chat conversation"
            aria-live="polite"
          >
            {agentThread.messages.length > 0 &&
              renderBlocks.map((block) =>
                block.kind === "task" ? (
                  <TaskSection
                    key={`task-${block.taskId}`}
                    taskId={block.taskId}
                    title={block.title}
                    done={block.concluded}
                    expandedTarget={block.taskId === targetTaskId}
                  >
                    {block.segments.map((segment) =>
                      renderSegment(segment, block.taskId),
                    )}
                  </TaskSection>
                ) : (
                  <React.Fragment
                    key={`loose-${block.segment.messageIndices[0]}`}
                  >
                    {renderSegment(block.segment)}
                  </React.Fragment>
                ),
              )}

            {pendingToolUses
              .filter(
                (toolUse) =>
                  toolUse.tool_use_id !== pendingApproval?.toolUse.tool_use_id,
              )
              .map((toolUse) => (
                <article
                  key={`pending-tool-${toolUse.tool_use_id}`}
                  className={styles.messageArticle}
                >
                  <AssistantToolUseContent toolUse={toolUse} />
                </article>
              ))}

            {/* Inline tool approval card */}
            {pendingApproval && onApprove && onDecline && (
              <article
                key={pendingApproval.toolUse.tool_use_id}
                className={styles.messageArticle}
              >
                <ToolApprovalCard
                  toolUse={pendingApproval.toolUse}
                  tool={pendingApproval.tool}
                  onApprove={onApprove}
                  onDecline={onDecline}
                  onEnableAutoMode={onEnableAutoMode}
                  savedPermissionsUnavailable={savedPermissionsUnavailable}
                />
              </article>
            )}

            {/* Thinking indicator */}
            <AgentWorkingIndicator
              thinking={thinking}
              activity={agentThread.activity}
              status={agentThread.status}
            />

            {/* Scroll sentinel - detects when scrolled to bottom of content */}
            <div
              ref={scrollSentinel.ref}
              aria-hidden="true"
              style={{ height: "1px", width: "100%", pointerEvents: "none" }}
            />

            {/* Spacer at bottom - height set via direct DOM manipulation */}
            <div ref={spacerRef} aria-hidden="true" style={{ height: 0 }} />
          </div>
        </div>
      </div>

      <div className={styles.scrollToBottomButtonContainer}>
        <ScrollToBottomButton
          show={!scrollSentinel.isVisible && !isAutoScrolling}
          loading={isGenerating}
          onClick={function scrollToBottom() {
            scrollSentinel.ref.current?.scrollIntoView({
              block: "end",
              behavior: "instant",
            });
          }}
        />
      </div>
    </div>
  );
};

/**
 * Shows the thinking dots, labeled with the thread's activity when one is set, whenever the view is thinking or the
 * thread has an activity. The activity is ignored while it is the user's turn, because it can outlive the turn: the
 * worker clears it with a write that may fail, and a worker stopped mid-summary never clears it.
 */
const AgentWorkingIndicator = ({
  thinking,
  activity,
  status,
}: {
  thinking: boolean;
  activity?: string | null;
  status: AgentThreadStatus;
}) => {
  const currentActivity =
    status === AgentThreadStatus.USER_TURN
      ? undefined
      : (activity ?? undefined);
  if (!thinking && !currentActivity) {
    return null;
  }
  return <ThinkingIndicator label={currentActivity} />;
};
